import classNames from 'classnames';
import React, { useState, useMemo, useEffect } from 'react';
import { Container, Card, CardHeader, CardBody, Collapse, TabContent, TabPane, Nav, NavItem, NavLink} from 'reactstrap';
import { Link } from 'react-router-dom';
import { getUnits } from '../shared/unitInfo';
import { ATTRIBUTE_ORDER, compareAttributeOrder } from '../shared/tierlistOrder';
import { CreateNukeTable } from './Tierlist/NukeTierlist';
import EmptyResults from './EmptyResults';
import SearchInput from './SearchInput';
import SkeletonImage from './SkeletonImage';
import { useDesktopAutofocus } from '../hooks/useDesktopAutofocus';
import PageMeta from './PageMeta';
import '../App.css';

const SCORE_FIELDS = [
    { key: "defense", label: "Defense" },
    { key: "artgen", label: "Arts Gen" },
    { key: "damage", label: "Damage" },
    { key: "buffs", label: "Buffs" },
    { key: "heal", label: "Heal" },
    { key: "break", label: "Break" },
];

function useIsCompactTier() {
    const [isCompact, setIsCompact] = useState(() =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
    );

    useEffect(() => {
        const media = window.matchMedia("(max-width: 768px)");
        const onChange = () => setIsCompact(media.matches);
        onChange();
        if (media.addEventListener) {
            media.addEventListener("change", onChange);
            return () => media.removeEventListener("change", onChange);
        }
        media.addListener(onChange);
        return () => media.removeListener(onChange);
    }, []);

    return isCompact;
}

export default function Tierlist() {
    //Tab hook
    const [currentTab, setCurrentTab] = useState('1');

    const toggle = tab => {
        if (currentTab !== tab) setCurrentTab(tab);
    }

    //Obtain Unit Data
    const units = getUnits();
    
    return (
        <Container className="gs-page">
            <PageMeta
                title="Tierlist"
                description="Grand Summoners global unit tierlist rankings for general and detailed performance categories."
                path="/tierlist"
            />
            <div className="gs-page-gutter">
                <DisplayHeader />
                <Nav pills justified className="gs-tabs">
                    <NavItem>
                        <NavLink 
                            className={classNames({ active: currentTab === '1' })}
                            onClick={() => { toggle('1') }}
                            >
                            General Tierlist
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink 
                            className={classNames({ active: currentTab === '2' })}
                            onClick={() => { toggle('2') }}
                            >
                            Detailed Tierlist
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink 
                            className={classNames({ active: currentTab === '3' })}
                            onClick={() => { toggle('3') }}
                            >
                            Nuke Tierlist
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent activeTab={currentTab}>
                    <TabPane tabId="1">
                        <DisplayGeneralTiers unit={units} />
                    </TabPane>
                    <TabPane tabId="2">
                        <CreateTable units={units} isActive={currentTab === '2'} />
                    </TabPane>
                    <TabPane tabId="3">
                        <CreateNukeTable />
                    </TabPane>
                </TabContent>
            </div>
        </Container>
    )
}


const RANK_LABELS = {
    1: "SS",
    2: "S+",
    3: "S",
    4: "A+",
    5: "A",
    6: "A-",
    7: "B+",
    8: "B",
    9: "B-",
    10: "C+",
    11: "C",
    12: "C-",
    13: "D",
    14: "F"
};

const DEFAULT_ATTRIBUTES = {
    Fire: true,
    Water: true,
    Earth: true,
    Light: true,
    Dark: true,
};

const SORT_COLUMNS = [
    { key: "rank", label: "Rank" },
    { key: "defense", label: "Defense" },
    { key: "artgen", label: "Arts Gen" },
    { key: "damage", label: "Damage" },
    { key: "buffs", label: "Buffs" },
    { key: "heal", label: "Heal" },
    { key: "break", label: "Break" },
];

const ATTRIBUTE_ICONS = {
    Fire: "/db/Icons/types/Fire_Icon.webp",
    Water: "/db/Icons/types/Water_Icon.webp",
    Earth: "/db/Icons/types/Earth_Icon.webp",
    Light: "/db/Icons/types/Light_Icon.webp",
    Dark: "/db/Icons/types/Dark_Icon.webp",
};

function sortTierRows(rows, key, direction) {
    return [...rows].sort((a, b) => {
        let result = 0;

        if (typeof a[key] === "number" && typeof b[key] === "number") {
            result = b[key] - a[key];
        } else if (key === "attribute") {
            result = compareAttributeOrder(a, b);
        } else {
            result = String(a[key]).localeCompare(String(b[key]));
        }

        if (direction === "desc") result *= -1;

        if (result === 0) {
            const attrDiff = compareAttributeOrder(a, b);
            if (attrDiff !== 0) return attrDiff;
        }

        if (result === 0) {
            return a.id - b.id;
        }

        return result;
    });
}

function CreateTable({ units, isActive = false }) {
    const [query, setQuery] = useState("");
    const [attributes, setAttributes] = useState(DEFAULT_ATTRIBUTES);
    const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "desc" });
    const isCompact = useIsCompactTier();
    const searchRef = useDesktopAutofocus(isActive);

    const rankedData = useMemo(() =>
        units
            .filter(char => char.tier && typeof char.tier.rank === "number")
            .map(char => ({
                ...char,
                rank: char.tier.rank,
                defense: char.tier.defense,
                artgen: char.tier.artgen,
                damage: char.tier.damage,
                buffs: char.tier.buffs,
                heal: char.tier.heal,
                break: char.tier.break
            })),
        [units]
    );

    const filteredData = useMemo(() => {
        const selectedAttributes = ATTRIBUTE_ORDER.filter((attr) => attributes[attr]);
        const normalizedQuery = query.trim().toLowerCase();

        return rankedData.filter((char) => {
            const matchesSearch = normalizedQuery === "" || char.name.toLowerCase().includes(normalizedQuery);
            const matchesAttribute = selectedAttributes.length > 0 && selectedAttributes.includes(char.attribute);
            return matchesSearch && matchesAttribute;
        });
    }, [rankedData, query, attributes]);

    const sortedData = useMemo(
        () => sortTierRows(filteredData, sortConfig.key, sortConfig.direction),
        [filteredData, sortConfig]
    );

    const handleSort = (key) => {
        setSortConfig((current) => {
            const direction = current.key === key && current.direction === "asc" ? "desc" : "asc";
            return { key, direction };
        });
    };

    const handleAttributeToggle = (attribute) => {
        setAttributes((current) => ({
            ...current,
            [attribute]: !current[attribute],
        }));
    };

    const clearFilters = () => {
        setQuery("");
        setAttributes({ ...DEFAULT_ATTRIBUTES });
    };

    const renderSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    return (
        <div className="gs-card gs-tier-table-panel">
            <div className="gs-controls-panel gs-tier-table-panel__controls">
                <SearchInput
                    inputRef={searchRef}
                    placeholder="Search Unit Name"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onClear={() => setQuery("")}
                    aria-label="Search Unit Name"
                />
                <div className="gs-options">
                    <div className="gs-field gs-tier-table-panel__attrs">
                        <span className="gs-field-label">Attribute</span>
                        <div className="filterButtons">
                            {ATTRIBUTE_ORDER.map((attribute) => (
                                <label
                                    key={attribute}
                                    className={attributes[attribute] ? "gs-chip is-active" : "gs-chip"}
                                >
                                    <input
                                        type="checkbox"
                                        name="attribute"
                                        value={attribute}
                                        checked={attributes[attribute]}
                                        onChange={() => handleAttributeToggle(attribute)}
                                    />
                                    <img
                                        src={ATTRIBUTE_ICONS[attribute]}
                                        alt={`${attribute} Attribute`}
                                        className="filterIcons"
                                    />
                                    {attribute}
                                </label>
                            ))}
                        </div>
                    </div>
                    {isCompact && (
                        <div className="gs-field gs-tier-table-panel__sort">
                            <span className="gs-field-label">Sort by</span>
                            <div className="gs-tier-table-panel__sort-row">
                                <select
                                    className="gs-select"
                                    value={sortConfig.key}
                                    onChange={(event) =>
                                        setSortConfig((current) => ({
                                            ...current,
                                            key: event.target.value,
                                        }))
                                    }
                                    aria-label="Sort detailed tierlist by"
                                >
                                    {SORT_COLUMNS.map(({ key, label }) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="gs-tier-table-panel__sort-dir"
                                    onClick={() =>
                                        setSortConfig((current) => ({
                                            ...current,
                                            direction: current.direction === "asc" ? "desc" : "asc",
                                        }))
                                    }
                                    aria-label={`Sort ${sortConfig.direction === "asc" ? "descending" : "ascending"}`}
                                >
                                    {sortConfig.direction === "asc" ? "↑ Asc" : "↓ Desc"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {sortedData.length === 0 ? (
                <EmptyResults
                    title="No units match your filters"
                    message="Try adjusting search or attribute filters, or clear them to see the full tierlist."
                    onClear={clearFilters}
                />
            ) : isCompact ? (
                <ul className="gs-tier-cards">
                    {sortedData.map((char) => (
                        <li key={char.id} className="gs-tier-card">
                            <div className="gs-tier-card__header">
                                <TierTableUnit unit={char} />
                                <span className={`gs-tier-table__rank gs-tier-table__rank--${char.rank}`}>
                                    {RANK_LABELS[char.rank] ?? "?"}
                                </span>
                            </div>
                            <dl className="gs-tier-card__stats">
                                {SCORE_FIELDS.map(({ key, label }) => (
                                    <div key={key} className="gs-tier-card__stat">
                                        <dt>{label}</dt>
                                        <dd>
                                            <span className="gs-tier-table__score">{Number(char[key]).toFixed(2)}</span>
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="gs-tier-table-scroll">
                    <table className="gs-tier-table">
                        <thead>
                            <tr>
                                <th className="gs-tier-table__name-col">Name</th>
                                {SORT_COLUMNS.map(({ key, label }) => (
                                    <th
                                        key={key}
                                        className={classNames("gs-tier-table__sortable", {
                                            "is-active": sortConfig.key === key,
                                        })}
                                        onClick={() => handleSort(key)}
                                        aria-sort={
                                            sortConfig.key === key
                                                ? sortConfig.direction === "asc"
                                                    ? "ascending"
                                                    : "descending"
                                                : "none"
                                        }
                                    >
                                        {label}{renderSortIndicator(key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((char) => (
                                <tr key={char.id}>
                                    <td>
                                        <TierTableUnit unit={char} />
                                    </td>
                                    <td>
                                        <span className={`gs-tier-table__rank gs-tier-table__rank--${char.rank}`}>
                                            {RANK_LABELS[char.rank] ?? "?"}
                                        </span>
                                    </td>
                                    <td><span className="gs-tier-table__score">{Number(char.defense).toFixed(2)}</span></td>
                                    <td><span className="gs-tier-table__score">{Number(char.artgen).toFixed(2)}</span></td>
                                    <td><span className="gs-tier-table__score">{Number(char.damage).toFixed(2)}</span></td>
                                    <td><span className="gs-tier-table__score">{Number(char.buffs).toFixed(2)}</span></td>
                                    <td><span className="gs-tier-table__score">{Number(char.heal).toFixed(2)}</span></td>
                                    <td><span className="gs-tier-table__score">{Number(char.break).toFixed(2)}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function TierTableUnit({ unit }) {
    const thumb = unit.image.thumbsuper || unit.image.thumbawk || unit.image.thumb5;

    return (
        <Link to={`/units/${unit.name}`} className="gs-tier-table__unit">
            <SkeletonImage
                src={thumb}
                alt={unit.name}
                loading="lazy"
                frameClassName="gs-skeleton-frame--tier-table"
            />
            <span>{unit.name}</span>
        </Link>
    );
}

function DisplayHeader(){
    const [noteOpen, setNoteOpen] = useState(false);

    return (
        <>
            <header className="gs-page-intro">
                <h1>Global Tierlist</h1>
                <p>Global units only. Rankings reflect optimized scenarios from Official Discord staff and are <b>NOT</b> the gospel; niches still matter.</p>
            </header>
            <Card className="gs-card gs-note-card">
                <CardHeader className="gs-card-header gs-note-card__header">
                    <button
                        type="button"
                        className={classNames("gs-note-card__toggle", { "is-open": noteOpen })}
                        onClick={() => setNoteOpen((open) => !open)}
                        aria-expanded={noteOpen}
                        aria-controls="tierlist-note-body"
                    >
                        <span>NOTE</span>
                        <span className="gs-note-card__chevron" aria-hidden="true" />
                    </button>
                </CardHeader>
                <Collapse isOpen={noteOpen}>
                    <CardBody id="tierlist-note-body" className="gs-card-body">
                        <p><center>This tierlist is for GLOBAL units only. Do <strong>not</strong> consider this gospel. Each unit may have their own niche - even the lower ranking units</center></p>
                        <p><center>This is simply meant to show the general analysis of each unit. </center></p>
                        <p><center>Rankings were determined by players from the Grand Summoners <a href="https://discord.gg/grandsummoners" target="_blank" rel="noopener noreferrer">Official Discord</a> Server Staff.</center></p>
                        <p><center>Be aware that the Rankings are based on their <b>most optimized</b> scenarios.</center></p>
                        <p><center><b>NOTE: </b>The "General Tierlist" tab displays all units.</center></p>
                        <p><center>The Detailed Tierlist tab will show all determined ratings.</center></p>
                        <p><center>To access the original document, and also obtain a copy, click <a href="https://docs.google.com/spreadsheets/d/1J6b7ptaZPZYFkd1p28X_RiP8QpCRiaQnS0bfMpVtah8/" target="_blank" rel="noopener noreferrer">HERE</a></center></p>
                    </CardBody>
                </Collapse>
            </Card>
        </>
    )
}

function DisplayGeneralTiers({unit}) {
    const rankedData = unit.filter(
        (char) => char.tier && typeof char.tier.rank === "number"
    );
    const ranks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

    return (
        <div className="gs-tier-sections">
            {ranks.map((rank) => (
                <DisplayRank key={rank} unit={rankedData} rank={rank} />
            ))}
        </div>
    );
}

function renderRankUnits(unitList, matchFn) {
    return ATTRIBUTE_ORDER.flatMap((attribute) =>
        unitList
            .filter((units) => matchFn(units) && units.attribute === attribute)
            .map((units) => <DisplayUnit unit={units} key={`${attribute}-${units.id || units.name}`} />)
    );
}

function DisplayRank({unit, rank}) {
    const unitsInRank = renderRankUnits(unit, (units) => units.tier.rank === rank);
    if (unitsInRank.length === 0) {
        return null;
    }

    return (
        <section className={`gs-tier-section gs-tier-section--${rank}`} aria-labelledby={`tier-rank-${rank}`}>
            <h2 id={`tier-rank-${rank}`} className="gs-tier-section__title">
                {RANK_LABELS[rank]}
            </h2>
            <div className="gs-tierlist-grid">
                {unitsInRank}
            </div>
        </section>
    );
}

function DisplayUnit({unit}) {
    const thumb = unit.image.thumbsuper || unit.image.thumbawk || unit.image.thumb5;

    return (
        <div className="gs-tierlist-unit">
            <Link to={`/units/${unit.name}`}>
                <SkeletonImage
                    src={thumb}
                    alt={unit.name}
                    loading="lazy"
                    frameClassName="gs-skeleton-frame--tierlist"
                />
                <span className="gs-thumb-name">{unit.name}</span>
            </Link>
        </div>
    )
}

